export interface InquiryPayload {
  name: string
  email: string
  business: string
  message: string
  website: string // honeypot — real visitors leave it empty
  turnstile_token: string
}

export interface ValidationErrors {
  [field: string]: string[]
}

export class ValidationError extends Error {
  errors: ValidationErrors

  constructor(errors: ValidationErrors) {
    super('Validation failed')
    this.errors = errors
  }
}

// /api/contact is a Lambda behind CloudFront. CloudFront signs the request
// on the way in (OAC), and a signed POST has to carry the SHA-256 of its body
// — Lambda doesn't accept unsigned payloads. Only the browser has the body,
// so it computes the hash. (crypto.subtle needs HTTPS: the form can't post
// from a plain-http dev server, and there's no local API to post to anyway.)
async function sha256Hex(body: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(body),
  )
  return Array.from(new Uint8Array(digest), (b) =>
    b.toString(16).padStart(2, '0'),
  ).join('')
}

export async function sendInquiry(payload: InquiryPayload): Promise<void> {
  const body = JSON.stringify(payload)
  const res = await fetch('/api/contact', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'x-amz-content-sha256': await sha256Hex(body),
    },
    body,
  })

  if (res.status === 422) {
    const body = (await res.json()) as { errors?: ValidationErrors }
    throw new ValidationError(body.errors ?? {})
  }

  // Success is the function's own answer, not any 2xx: a request CloudFront
  // rejects comes back as the SPA's index.html with a 200 (its 403/404
  // fallback covers every path), and that must read as a failure.
  const result = (await res.json().catch(() => null)) as { ok?: boolean } | null
  if (res.status !== 201 || result?.ok !== true) {
    throw new Error(`Inquiry failed with status ${res.status}`)
  }
}
